import {
  getFlow,
  getGetFlowQueryKey,
  saveFlowDraft,
} from '@reelplicate/api-client/flows';
import type { FlowDto } from '@reelplicate/api-client/model';
import { type FlowGraph, flowGraphSchema } from '@reelplicate/flows';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';

import type { EditorStore } from './editor-store';

/** Quiet time after the last edit before saving. */
export const AUTOSAVE_DELAY_MS = 800;
const RETRY_MS = 5000;

export type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'conflict' | 'failed';

type Options = {
  store: EditorStore;
  revision: number;
  send: (graph: FlowGraph, revision: number) => Promise<FlowDto>;
  fetch: () => Promise<FlowDto>;
  onStatus: (status: SaveStatus) => void;
  onSaved: (flow: FlowDto) => void;
  onConflict: () => void;
};

/**
 * The save loop, apart from React: one request at a time, each sending the
 * revision the last one returned. A 409 stops it until `reload`.
 */
export function createAutosaver({
  store,
  revision: initial,
  send,
  fetch,
  onStatus,
  onSaved,
  onConflict,
}: Options) {
  let revision = initial;
  let savedVersion = store.getState().version;
  let inFlight: Promise<void> | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const schedule = (delay = AUTOSAVE_DELAY_MS) => {
    clearTimeout(timer);
    timer = setTimeout(() => void save(), delay);
  };

  async function attempt(graph: FlowGraph, version: number) {
    try {
      const saved = await send(graph, revision);
      revision = saved.revision;
      savedVersion = version;
      onSaved(saved);
      onStatus(store.getState().version === version ? 'saved' : 'unsaved');
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        stopped = true;
        onStatus('conflict');
        onConflict();
        return;
      }
      onStatus('failed');
      schedule(RETRY_MS);
    }
  }

  async function save(): Promise<void> {
    clearTimeout(timer);
    if (stopped) return;
    if (inFlight) {
      await inFlight;
      return save();
    }
    const { graph, version } = store.getState();
    if (version === savedVersion) return;
    onStatus('saving');
    inFlight = attempt(graph, version).finally(() => {
      inFlight = null;
    });
    await inFlight;
    if (!stopped && store.getState().version !== savedVersion) schedule();
  }

  return {
    /** An edit happened: save after a quiet moment. */
    changed: () => {
      if (stopped) return;
      onStatus('unsaved');
      schedule();
    },
    /** Saves now; resolves true when everything is saved. */
    flush: async () => {
      await save();
      return !stopped && store.getState().version === savedVersion;
    },
    /** Replaces the editor with the server's copy (after a conflict). */
    reload: async () => {
      const fresh = await fetch();
      store.getState().reset(flowGraphSchema.parse(fresh.draftGraph));
      revision = fresh.revision;
      savedVersion = store.getState().version;
      stopped = false;
      onSaved(fresh);
      onStatus('saved');
    },
    dispose: () => {
      clearTimeout(timer);
    },
  };
}

/**
 * Saves the editor's graph a moment after each change (see `createAutosaver`),
 * keeps the flow query in step, and warns before leaving with unsaved edits.
 */
export function useAutosave({
  workspaceId,
  flow,
  store,
}: {
  workspaceId: string;
  flow: FlowDto;
  store: EditorStore;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SaveStatus>('saved');
  const [savedAt, setSavedAt] = useState(() => new Date(flow.updatedAt));

  const [saver] = useState(() => {
    const key = getGetFlowQueryKey(workspaceId, flow.id);
    const saver = createAutosaver({
      store,
      revision: flow.revision,
      send: (graph, revision) => saveFlowDraft(workspaceId, flow.id, { graph, revision }),
      fetch: () => getFlow(workspaceId, flow.id),
      onStatus: setStatus,
      onSaved: (saved) => {
        setSavedAt(new Date(saved.updatedAt));
        queryClient.setQueryData(key, saved);
      },
      onConflict: () =>
        toast.error('This flow was changed somewhere else', {
          description:
            'Your latest edits aren’t saved. Reload to get the newest version.',
          action: { label: 'Reload', onClick: () => void saver.reload() },
        }),
    });
    return saver;
  });

  useEffect(() => {
    const unsubscribe = store.subscribe((state, previous) => {
      if (state.version !== previous.version) saver.changed();
    });
    return () => {
      unsubscribe();
      saver.dispose();
    };
  }, [store, saver]);

  const pending = status === 'unsaved' || status === 'saving' || status === 'failed';
  useEffect(() => {
    if (!pending) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending]);

  return { status, savedAt, flush: saver.flush, reload: saver.reload };
}
